import { useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/common";

function Rows({ label, rows, fields, onChange, testid }) {
  const set = (n, k, v) => onChange(rows.map((r, i) => (i === n ? { ...r, [k]: v } : r)));
  return (
    <div data-testid={testid}>
      <p className="mb-2 text-xs font-bold text-q-muted">{label}</p>
      <div className="space-y-2">
        {rows.map((r, n) => (
          <div key={n} className="flex flex-wrap items-center gap-2 rounded-xl border border-q-line p-2">
            {fields.map(([k, ph, opts]) => (opts ? (
              <select key={k} className="q-input min-w-0 flex-1 basis-24" value={r[k] || opts[0]} onChange={(e) => set(n, k, e.target.value)} data-testid={`${testid}-${k}-${n}`}>{opts.map((o) => <option key={o}>{o}</option>)}</select>
            ) : (
              <input key={k} className="q-input min-w-0 flex-1 basis-32" placeholder={ph} value={r[k] || ""} onChange={(e) => set(n, k, e.target.value)} data-testid={`${testid}-${k}-${n}`} />
            )))}
            <button className="q-btn q-btn-ghost w-11 px-0" aria-label="Remove" onClick={() => onChange(rows.filter((_, i) => i !== n))} data-testid={`${testid}-remove-${n}`}><X className="h-4 w-4" /></button>
          </div>
        ))}
        <button className="q-btn q-btn-ghost" onClick={() => onChange([...rows, {}])} data-testid={`${testid}-add`}><Plus className="h-4 w-4" /> Add</button>
      </div>
    </div>
  );
}

export function CardEditor({ hubId, member, bloodTypes, onClose, onSaved }) {
  const c = member.card;
  const [f, setF] = useState({ ...c, sitter_notes: { ...c.sitter_notes } });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/organizations/${hubId}/emergency/cards/${member.key}`, { ...f, dob: f.dob || null });
      toast.success("Card saved"); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent bigClose className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="card-editor">
        <DialogHeader><DialogTitle className="font-display">{member.name}'s card</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Rows label="Allergies" rows={f.allergies} onChange={(v) => set("allergies", v)} testid="edit-allergies"
            fields={[["allergen", "Allergen (e.g. Peanuts)"], ["severity", "", ["Mild", "Severe"]], ["note", "Reaction / treatment"]]} />
          <Field label="Medical conditions"><textarea className="q-input min-h-[60px] w-full" value={f.conditions} onChange={(e) => set("conditions", e.target.value)} data-testid="edit-conditions" /></Field>
          <Rows label="Medications" rows={f.medications} onChange={(v) => set("medications", v)} testid="edit-meds" fields={[["name", "Name"], ["dose", "Dose"], ["when", "When"]]} />
          <div className="flex flex-wrap gap-3">
            <Field label="Blood type"><select className="q-input" value={f.blood_type} onChange={(e) => set("blood_type", e.target.value)} data-testid="edit-blood">{bloodTypes.map((b) => <option key={b}>{b}</option>)}</select></Field>
            <Field label="Date of birth"><input type="date" className="q-input" value={f.dob || ""} onChange={(e) => set("dob", e.target.value)} data-testid="edit-dob" /></Field>
            {member.kind === "adult" && <Field label="Phone"><input className="q-input" value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="edit-phone" /></Field>}
          </div>
          <Rows label="Doctors" rows={f.doctors} onChange={(v) => set("doctors", v)} testid="edit-doctors" fields={[["role", "Role"], ["name", "Name"], ["phone", "Phone"]]} />
          <Field label="Insurance notes"><textarea className="q-input min-h-[60px] w-full" value={f.insurance} onChange={(e) => set("insurance", e.target.value)} data-testid="edit-insurance" /></Field>
          <p className="text-xs font-bold text-q-muted">Sitter notes</p>
          {[["bedtime", "Bedtime"], ["screen", "Screen rules"], ["food", "Food rules"]].map(([k, l]) => (
            <Field key={k} label={l}><input className="q-input w-full" value={f.sitter_notes[k] || ""} onChange={(e) => set("sitter_notes", { ...f.sitter_notes, [k]: e.target.value })} data-testid={`edit-sitter-${k}`} /></Field>
          ))}
          <button className="q-btn q-btn-primary w-full" disabled={busy} onClick={save} data-testid="card-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save card</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function FamilyEditor({ hubId, family, onClose, onSaved }) {
  const [f, setF] = useState({ address: family.address, contacts: family.contacts, hospital: { ...family.hospital } });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/organizations/${hubId}/emergency/family`, f); toast.success("Family section saved"); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const hosp = (k, v) => setF((x) => ({ ...x, hospital: { ...x.hospital, [k]: v } }));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent bigClose className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="family-editor">
        <DialogHeader><DialogTitle className="font-display">Family section</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Home address"><input className="q-input w-full" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="family-address-input" /></Field>
          <Rows label="Emergency contacts" rows={f.contacts} onChange={(v) => setF({ ...f, contacts: v })} testid="family-contacts" fields={[["name", "Name"], ["relationship", "Relationship"], ["phone", "Phone"]]} />
          <p className="text-xs font-bold text-q-muted">Nearest hospital</p>
          {[["name", "Name"], ["address", "Address"], ["phone", "Phone"]].map(([k, l]) => <input key={k} className="q-input w-full" placeholder={l} value={f.hospital[k] || ""} onChange={(e) => hosp(k, e.target.value)} data-testid={`family-hospital-${k}`} />)}
          <button className="q-btn q-btn-primary w-full" disabled={busy} onClick={save} data-testid="family-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
